import { createFileRoute, notFound } from '@tanstack/react-router'
import { ArrowRight, FileSearch, FileText } from 'lucide-react'
import { AppShell } from '@/components/AppShell'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'

/**
 * Dev-only harness for mobile-nav smoke testing AND visual review of the
 * authenticated chrome. Renders AppShell without the Logto auth guard so
 * Playwright can drive viewport-rendering assertions without simulating
 * the full hosted-login dance.
 *
 * Gated on `import.meta.env.DEV` so production builds 404 the route.
 * Referenced by:
 *   - `frontend/scripts/smoke-test-mobile-nav.ts` — keys off the role/name
 *     pair on the nav links and the `data-testid="content"` marker below.
 *   - Manual screenshots for the responsive-shell task (`smoke-test-evidence/`).
 */
export const Route = createFileRoute('/dev/app-shell-preview')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: AppShellPreview,
})

const QUICK_LINKS = [
  {
    title: 'Doc Filler',
    description:
      'Upload a PDF or DOCX and the agent extracts and pre-fills the fields for you.',
    icon: FileText,
  },
  {
    title: 'Search documents',
    description:
      'Find documents by fields, values, context, and extracted text.',
    icon: FileSearch,
  },
] as const

function AppShellPreview() {
  return (
    <AppShell>
      <div
        data-testid="content"
        className="mx-auto w-full max-w-5xl space-y-8 px-4 py-8 md:px-8"
      >
        <PageHeader
          eyebrow="Preview"
          title="App shell preview"
          description="Dev-only harness used by the mobile-nav smoke test and screenshot evidence. Renders the real AppShell chrome without an auth guard."
          actions={
            <Button>
              Sample action
              <ArrowRight />
            </Button>
          }
        />

        <section className="space-y-3">
          <SectionHeader
            title="Sample cards"
            description="The shell uses Aurora design-system primitives — these cards are stand-ins for real route content."
          />
          <div className="grid gap-3 md:grid-cols-3">
            {QUICK_LINKS.map(({ title, description, icon: Icon }) => (
              <Card key={title} interactive className="aurora-card-hover h-full">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
                    >
                      <Icon className="size-4" />
                    </span>
                    <div className="min-w-0 space-y-1">
                      <CardTitle>{title}</CardTitle>
                      <CardDescription>{description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <SectionHeader
            title="Status row"
            description="Sample badges to show how status colour pairs render against the shell background."
          />
          <Card>
            <CardContent className="flex flex-wrap items-center gap-2 pt-6">
              <Badge tone="info">Loading</Badge>
              <Badge tone="success">Filled</Badge>
              <Badge tone="warning">Needs review</Badge>
              <Badge tone="danger">Error</Badge>
            </CardContent>
          </Card>
        </section>
      </div>
    </AppShell>
  )
}
